import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGEMENT_OPTIONS = ["Appartement", "Maison", "Studio", "Autre"];
const RENOVATION_OPTIONS = [
  "Rénovation complète",
  "Cuisine",
  "Salle de bains",
  "Électricité et plomberie",
  "Peinture et revêtements",
  "Menuiserie et finitions",
  "Autre",
];

const initialForm = {
  nom: "",
  telephone: "",
  email: "",
  localite: "",
  type_logement: "",
  type_renovation: "",
  description: "",
  website: "",
};

const inputClass =
  "w-full rounded-sm border border-slate-300 bg-white px-4 py-3 text-sm text-ink placeholder:text-slate-400 transition-[border-color,box-shadow] duration-200 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15";

const Field = ({ label, error, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-ink">
      {label}
    </label>
    {children}
    {error && (
      <p data-testid={`${htmlFor}-error`} className="mt-1.5 text-xs font-medium text-red-600" role="alert">
        {error}
      </p>
    )}
  </div>
);

export const QuoteForm = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((err) => ({ ...err, [key]: undefined }));
  };

  const validate = () => {
    const err = {};
    if (form.nom.trim().length < 2) err.nom = "Ce champ est requis.";
    if (!/^[+0-9][0-9 ()./-]{5,}$/.test(form.telephone.trim()))
      err.telephone = "Numéro de téléphone invalide.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim()))
      err.email = "Adresse e-mail invalide.";
    if (form.localite.trim().length < 2) err.localite = "Ce champ est requis.";
    if (!form.type_logement) err.type_logement = "Veuillez sélectionner une option.";
    if (!form.type_renovation) err.type_renovation = "Veuillez sélectionner une option.";
    if (form.description.trim().length < 10)
      err.description = "Merci de décrire votre projet (10 caractères minimum).";
    return err;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (Object.keys(err).length > 0) {
      setErrors(err);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/devis`, {
        ...form,
        nom: form.nom.trim(),
        telephone: form.telephone.trim(),
        email: form.email.trim(),
        localite: form.localite.trim(),
        description: form.description.trim(),
      });
      setSent(true);
    } catch (error) {
      toast.error(
        "Une erreur est survenue. Veuillez réessayer ou nous appeler au +41 78 213 59 44."
      );
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div
        id="devis"
        data-testid="devis-success"
        className="flex h-full flex-col items-center justify-center rounded-sm border border-brand/25 bg-brand-light p-10 text-center lg:min-h-[560px]"
      >
        <CheckCircle2 className="mb-5 h-12 w-12 text-brand" strokeWidth={1.4} />
        <h3 className="font-display text-2xl font-medium tracking-tight text-ink">
          Demande envoyée
        </h3>
        <p className="mt-4 max-w-sm text-base leading-relaxed text-slate-700">
          Merci pour votre demande. Nous vous contacterons dans les meilleurs délais.
        </p>
        <button
          type="button"
          onClick={() => {
            setForm(initialForm);
            setSent(false);
          }}
          data-testid="devis-new-request-button"
          className="mt-8 rounded-full border border-brand px-6 py-2.5 text-sm font-semibold text-brand transition-colors hover:bg-brand hover:text-white"
        >
          Envoyer une autre demande
        </button>
      </div>
    );
  }

  return (
    <form
      id="devis"
      onSubmit={onSubmit}
      noValidate
      data-testid="devis-form"
      className="rounded-sm border border-slate-200 bg-mist p-6 shadow-[0_18px_45px_rgba(15,23,42,0.05)] sm:p-9"
    >
      <h3 className="font-display text-2xl font-medium tracking-tight text-ink">
        Demande de devis
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">
        Sans engagement — nous étudions chaque projet avec attention.
      </p>

      <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field label="Nom et prénom" error={errors.nom} htmlFor="nom">
          <input
            id="nom"
            type="text"
            value={form.nom}
            onChange={set("nom")}
            data-testid="devis-nom-input"
            className={inputClass}
            placeholder="Votre nom complet"
            autoComplete="name"
          />
        </Field>
        <Field label="Téléphone" error={errors.telephone} htmlFor="telephone">
          <input
            id="telephone"
            type="tel"
            value={form.telephone}
            onChange={set("telephone")}
            data-testid="devis-telephone-input"
            className={inputClass}
            placeholder="+41 …"
            autoComplete="tel"
          />
        </Field>
        <Field label="Adresse e-mail" error={errors.email} htmlFor="email">
          <input
            id="email"
            type="email"
            value={form.email}
            onChange={set("email")}
            data-testid="devis-email-input"
            className={inputClass}
            placeholder="vous@exemple.ch"
            autoComplete="email"
          />
        </Field>
        <Field label="Localité" error={errors.localite} htmlFor="localite">
          <input
            id="localite"
            type="text"
            value={form.localite}
            onChange={set("localite")}
            data-testid="devis-localite-input"
            className={inputClass}
            placeholder="Genève, Carouge, Lancy…"
            autoComplete="address-level2"
          />
        </Field>
        <Field label="Type de logement" error={errors.type_logement} htmlFor="type_logement">
          <select
            id="type_logement"
            value={form.type_logement}
            onChange={set("type_logement")}
            data-testid="devis-logement-select"
            className={`${inputClass} ${form.type_logement ? "" : "text-slate-400"}`}
          >
            <option value="" disabled>
              Sélectionnez…
            </option>
            {LOGEMENT_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Type de rénovation" error={errors.type_renovation} htmlFor="type_renovation">
          <select
            id="type_renovation"
            value={form.type_renovation}
            onChange={set("type_renovation")}
            data-testid="devis-renovation-select"
            className={`${inputClass} ${form.type_renovation ? "" : "text-slate-400"}`}
          >
            <option value="" disabled>
              Sélectionnez…
            </option>
            {RENOVATION_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="mt-5">
        <Field label="Description du projet" error={errors.description} htmlFor="description">
          <textarea
            id="description"
            rows={4}
            value={form.description}
            onChange={set("description")}
            data-testid="devis-description-textarea"
            className={`${inputClass} resize-y`}
            placeholder="Décrivez brièvement votre projet de rénovation…"
          />
        </Field>
      </div>

      <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
        <label htmlFor="website">Ne pas remplir</label>
        <input
          id="website"
          type="text"
          value={form.website}
          onChange={set("website")}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <button
        type="submit"
        disabled={sending}
        data-testid="devis-submit-button"
        className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-white transition-[background-color,color,transform,opacity] duration-200 hover:bg-sun hover:text-ink active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {sending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
            Envoi en cours…
          </>
        ) : (
          <>
            <Send className="h-4 w-4" strokeWidth={1.8} />
            Envoyer ma demande
          </>
        )}
      </button>
    </form>
  );
};
